import type { ISupportMeasureReportConfiguration } from '@/interfaces';
import {
  ESupportMeasureReportCoverSheet,
  ESupportMeasureReportSignatory,
} from 'common/enum/support.measure.report.enum';
import { isEnumValue } from 'common/utils/enum.utils';
import { defineStore } from 'pinia';

const DEFAULT_SUPPORT_MEASURE_REPORT_CONFIGURATION: Readonly<ISupportMeasureReportConfiguration> = {
  coverSheet: ESupportMeasureReportCoverSheet.PLANT,
  includeCompetenceDevelopment: true,
  includeCompletionNote: true,
  includeMaterials: true,
  includeSignatures: false,
  signatories: [ESupportMeasureReportSignatory.TEACHER, ESupportMeasureReportSignatory.PARENTS],
};

const isSignatory = (value: unknown): value is ESupportMeasureReportSignatory =>
  isEnumValue(ESupportMeasureReportSignatory, value);

export const useSupportMeasureReportConfigurationStore = defineStore('supportMeasureReportConfiguration', {
  state: (): ISupportMeasureReportConfiguration => ({
    ...DEFAULT_SUPPORT_MEASURE_REPORT_CONFIGURATION,
    signatories: [...DEFAULT_SUPPORT_MEASURE_REPORT_CONFIGURATION.signatories],
  }),

  getters: {
    // the persisted state can stem from an older release, so values the enums no longer know fall back or drop out
    selectedConfiguration: (state): ISupportMeasureReportConfiguration => ({
      coverSheet: isEnumValue(ESupportMeasureReportCoverSheet, state.coverSheet)
        ? state.coverSheet
        : DEFAULT_SUPPORT_MEASURE_REPORT_CONFIGURATION.coverSheet,
      includeCompetenceDevelopment: state.includeCompetenceDevelopment,
      includeCompletionNote: state.includeCompletionNote,
      includeMaterials: state.includeMaterials,
      includeSignatures: state.includeSignatures,
      signatories: state.signatories.filter(isSignatory),
    }),
  },

  actions: {
    setSelectedConfiguration(selected: ISupportMeasureReportConfiguration) {
      this.$patch({ ...selected, signatories: [...selected.signatories] });
    },
  },

  persist: {
    key: 'support-measure-report-configuration-store',
  },
});
